import React, { useState } from 'react';
import AdminRowIcon from '../../components/admin/AdminRowIcon';
import { radioOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonSearchbar,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonButton,
  IonSpinner,
  IonText,
  IonAlert,
  IonToast,
} from '@ionic/react';
import { useAdminDevices } from '../../../application/use-cases/admin/useAdminDevices';
import { ConnectivityBadge } from '../../components/ConnectivityStatus';
import AdminListFooter from '../../components/admin/AdminListFooter';
import './Admin.css';
import { componentsSummary } from '../../../domain/Component';


const AdminDevices: React.FC = () => {
  const {
    query,
    setQuery,
    loading,
    error,
    busyId,
    renameTarget,
    setRenameTarget,
    deleteTarget,
    setDeleteTarget,
    handleRename,
    handleDelete,
    transferTarget,
    setTransferTarget,
    handleTransfer,
    shareTarget,
    setShareTarget,
    handleShare,
    notice,
    clearNotice,
    filtered,
    total,
    loadingMore,
    loadMore,
    restoreDevice,
  } = useAdminDevices();
  const history = useHistory();
  const [openSharesId, setOpenSharesId] = useState<string | null>(null);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin" />
          </IonButtons>
          <IonTitle>Estaciones ({total.toLocaleString('es-AR')})</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={query}
            placeholder="Buscar por nombre o dueño"
            onIonInput={(e) => setQuery(e.detail.value ?? '')}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        <IonList className="admin-list">
          {filtered.map((d) => (
            <IonItem key={d.id} lines="full">
              <AdminRowIcon icon={radioOutline} tone={d.deletedAt ? 'grey' : d.status === 'online' ? 'green' : 'orange'} />
              <IonLabel>
                <h2>{d.name}</h2>
                <p>
                  {componentsSummary(d.components)} · {d.ownerName} ({d.ownerEmail})
                </p>
                <p className="admin-list-row-meta">
                  {d.mqttTopic} · 
                  {d.sharedWith.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="admin-shares-toggle"
                        onClick={() => setOpenSharesId(openSharesId === d.id ? null : d.id)}
                      >
                        Compartido con {d.sharedWith.length} más {openSharesId === d.id ? '▴' : '▾'}
                      </button>
                      {' · '}
                    </>
                  )}
                  desde {new Date(d.createdAt).toLocaleDateString('es-AR')}
                </p>
                {openSharesId === d.id && (
                  <ul className="admin-shares-list">
                    {d.sharedWith.map((u) => (
                      <li key={u.email}>
                        {u.name} ({u.email}) · {u.level === 'control' ? 'Ver y controlar' : 'Solo ver'}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="admin-row-actions">
                  {d.deletedAt ? (
                    <IonButton
                      size="small"
                      fill="clear"
                      color="success"
                      disabled={busyId === d.id}
                      onClick={() => restoreDevice(d.id)}
                    >
                      Restaurar
                    </IonButton>
                  ) : (
                    <>
                      <IonButton size="small" fill="clear" onClick={() => history.push(`/app/admin/devices/${d.id}/mqtt`)}>
                        Origen de datos
                      </IonButton>
                      <IonButton size="small" fill="clear" disabled={busyId === d.id} onClick={() => setRenameTarget(d)}>
                        Renombrar
                      </IonButton>
                      <IonButton size="small" fill="clear" disabled={busyId === d.id} onClick={() => setShareTarget(d)}>
                        Dar acceso
                      </IonButton>
                      <IonButton size="small" fill="clear" disabled={busyId === d.id} onClick={() => setTransferTarget(d)}>
                        Cambiar dueño
                      </IonButton>
                      <IonButton
                        size="small"
                        fill="clear"
                        color="danger"
                        disabled={busyId === d.id}
                        onClick={() => setDeleteTarget(d)}
                      >
                        Eliminar
                      </IonButton>
                    </>
                  )}
                </div>
              </IonLabel>
              {d.deletedAt ? (
                <IonBadge color="danger">Eliminado</IonBadge>
              ) : (
                <ConnectivityBadge device={d} />
              )}
            </IonItem>
          ))}
        </IonList>
        {!loading && <AdminListFooter shown={filtered.length} total={total} loadingMore={loadingMore} onMore={loadMore} />}

        <IonAlert
          isOpen={!!renameTarget}
          onDidDismiss={() => setRenameTarget(null)}
          header="Renombrar estación"
          inputs={[{ name: 'name', type: 'text', value: renameTarget?.name, placeholder: 'Nombre' }]}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Guardar', handler: (data) => handleRename(data.name ?? '') },
          ]}
        />

        <IonAlert
          isOpen={!!shareTarget}
          onDidDismiss={() => setShareTarget(null)}
          header="Dar acceso"
          message={`"${shareTarget?.name}" sigue siendo de ${shareTarget?.ownerName}; el usuario la ve en su app (y en el asistente) sin aceptar ninguna invitación.`}
          inputs={[{ name: 'email', type: 'email', placeholder: 'Email del usuario' }]}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Solo ver', handler: (data) => handleShare(data.email ?? '', 'view') },
            { text: 'Ver y controlar', handler: (data) => handleShare(data.email ?? '', 'control') },
          ]}
        />

        <IonAlert
          isOpen={!!transferTarget}
          onDidDismiss={() => setTransferTarget(null)}
          header="Cambiar dueño"
          message={`"${transferTarget?.name}" pasa a ser del usuario y ${transferTarget?.ownerName} deja de verla. Sale de su campo y su lote; las alertas siguen, prendidas para el nuevo dueño.`}
          inputs={[{ name: 'email', type: 'email', placeholder: 'Email del nuevo dueño' }]}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Pasar estación', role: 'destructive', handler: (data) => handleTransfer(data.email ?? '') },
          ]}
        />

        <IonToast isOpen={!!notice} message={notice ?? ''} duration={3000} onDidDismiss={clearNotice} />

        <IonAlert
          isOpen={!!deleteTarget}
          onDidDismiss={() => setDeleteTarget(null)}
          header="¿Eliminar estación?"
          message={`"${deleteTarget?.name}" va a desaparecer de la app de ${deleteTarget?.ownerName}. Se puede restaurar después desde acá.`}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Eliminar', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default AdminDevices;
